import { Col, Divider, Icon, P4PDayGiftBadge, Row, Sidepanel, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

const SCROLL_ID = 'p4pday-fans-scroll';
const PAGE_SIZE = 25;
const PAGE_WINDOW = 2; // page numbers shown each side of the current one
const SEARCH_DEBOUNCE_MS = 300;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${theme.GREY_10};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover {
    background: ${theme.GREY_20};
  }
`;

const Card = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid ${theme.GREY_20};
  background: #fff;
`;

const Chip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(props) => props.col};
  background: ${(props) => props.bg};
`;

const Code = styled.span`
  font-family: monospace;
  letter-spacing: 1px;
  background-color: #f2f4f7;
  border-radius: 4px;
  padding: 1px 6px;
`;

const ActionBtn = styled.button`
  border: none;
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  color: ${(props) => (props.primary ? '#fff' : theme.GREY_80)};
  background: ${(props) => (props.primary ? theme.SECONDARY : theme.GREY_10)};
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageBtn = styled.button`
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${(props) => (props.active ? theme.GREY_70 : theme.GREY_20)};
  color: ${(props) => (props.active ? '#fff' : theme.GREY_70)};
  background: ${(props) => (props.active ? theme.GREY_70 : '#fff')};
  &:hover:not(:disabled) {
    background: ${(props) => (props.active ? theme.GREY_70 : theme.GREY_10)};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/** Same windowing as the ActivityLog and Reporting pagers: 1 … 4 5 [6] 7 8 … 20. */
function getPageNumbers(page, totalPages) {
  const pages = [];
  const start = Math.max(1, page - PAGE_WINDOW);
  const end = Math.min(totalPages, page + PAGE_WINDOW);

  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push('...');
  }
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages) {
    if (end < totalPages - 1) pages.push('...');
    pages.push(totalPages);
  }
  return pages;
}

function fmt(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

/**
 * One fan holding the badge, with the revoke action and its confirm step inline.
 *
 * The confirm lives in the row rather than in a modal: Sidepanel and ModalV2 stack awkwardly,
 * and the admin should be able to see whose badge they are about to remove while confirming.
 */
const FanRow = ({ fan, showBadge, onRevoked }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const p4pDay = fan.p4pDay || {};
  const handedOver = !!p4pDay.giftClaimed;

  async function revoke() {
    setBusy(true);
    // Not silent: a refusal is toasted with the server's message and resolves falsy.
    const res = await Api.put('/admin/p4pday/revoke', { userId: fan._id });
    setBusy(false);
    setConfirming(false);
    if (!res) return;

    dispatch({
      type: 'HANDLE_RES',
      payload: {
        // Not restocked for one of two reasons: the gift already left the booth, or the badge
        // predates numbering and has no number to hand back with the stock.
        res: res.data?.returnedToStock
          ? t('admin.p4pDay.fans.revokedRestocked')
          : handedOver
            ? t('admin.p4pDay.fans.revokedNotRestocked')
            : t('admin.p4pDay.fans.revokedUnnumbered')
      }
    });
    onRevoked(fan._id);
  }

  return (
    <Card>
      <Row between center wid="100%" gap="12px">
        <Row center gap="10px" style={{ minWidth: 0 }}>
          {showBadge && <P4PDayGiftBadge giftColor={p4pDay.giftColor} />}
          <Col start style={{ minWidth: 0 }}>
            <Text b2 bold marg="0" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {fan.name || fan.username || fan.email}
            </Text>
            {fan.username && (
              <a
                href={`/profile/${fan.username}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '12px', color: theme.GREY_60 }}
              >
                @{fan.username}
              </a>
            )}
          </Col>
        </Row>
        <Text b2 bold marg="0" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {p4pDay.badgeNo ? `#${p4pDay.badgeNo}` : '—'}
        </Text>
      </Row>

      <Row center wrapFlex gap="8px" marg="10px 0 0">
        <Text c1 col={theme.GREY_60} marg="0">
          {t('admin.p4pDay.fans.fanId')} <Code>{p4pDay.fanId || '—'}</Code>
        </Text>
        <Text c1 col={theme.GREY_60} marg="0">
          · {t('admin.p4pDay.fans.awardedOn', { date: fmt(p4pDay.awardedAt) })}
        </Text>
        {p4pDay.awardedBy && (
          <Chip col={theme.DARK_PURPLE} bg="#F1EBFB">
            {t('admin.p4pDay.fans.byAdmin')}
          </Chip>
        )}
        {handedOver && (
          <Chip col="#067647" bg="#ECFDF3">
            {t('admin.p4pDay.fans.handedOver')}
          </Chip>
        )}
      </Row>

      {confirming ? (
        <Col start wid="100%" marg="12px 0 0">
          {handedOver && (
            <Text c1 bold col={theme.SECONDARY} marg="0 0 6px" style={{ lineHeight: 1.5 }}>
              {t('admin.p4pDay.fans.handedOverWarning')}
            </Text>
          )}
          <Text c1 col={theme.GREY_60} marg="0 0 10px" style={{ lineHeight: 1.5 }}>
            {handedOver ? t('admin.p4pDay.fans.confirmNoRestock') : t('admin.p4pDay.fans.confirm')}
          </Text>
          <Row gap="8px">
            <ActionBtn type="button" primary disabled={busy} onClick={revoke}>
              {t('admin.p4pDay.fans.confirmButton')}
            </ActionBtn>
            <ActionBtn type="button" disabled={busy} onClick={() => setConfirming(false)}>
              {t('admin.p4pDay.fans.cancel')}
            </ActionBtn>
          </Row>
        </Col>
      ) : (
        <Row marg="12px 0 0" style={{ justifyContent: 'flex-end' }}>
          <ActionBtn type="button" onClick={() => setConfirming(true)}>
            {t('admin.p4pDay.fans.delete')}
          </ActionBtn>
        </Row>
      )}
    </Card>
  );
};

/**
 * The fans holding a P4P Day badge — one colour, or every colour when `color` is null.
 *
 * Opened from a badge card on the P4P Day settings tab. Reads the booth endpoint
 * (`/admin/p4pday/claimed`), which already excludes revoked fans and fans whose claim found no
 * stock, so every row here is someone holding a real badge.
 */
const P4PDayFansPanel = ({ visible, color, title, onClose, onChanged }) => {
  const { t } = useTranslation();
  const [fans, setFans] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  // `query` is what is in the box; `search` is what was last sent, a beat behind the typing so
  // each keystroke is not its own request.
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  // Bumped on every request, so a slow response for a page (or card) the admin has already left
  // cannot overwrite the one they are looking at.
  const requestKey = useRef(0);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadPage = useCallback(
    async (target) => {
      requestKey.current += 1;
      const key = requestKey.current;
      setLoading(true);
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String((target - 1) * PAGE_SIZE)
      });
      if (color) params.set('color', color);
      if (search) params.set('search', search);

      const res = await Api.get(`/admin/p4pday/claimed?${params.toString()}`);
      if (key !== requestKey.current) return;
      setLoading(false);
      if (!res?.data) return;

      const nextTotal = res.pagination?.total ?? 0;
      // The last row of the last page was just revoked: step back rather than show an empty page.
      if (res.data.length === 0 && target > 1 && nextTotal > 0) {
        loadPage(Math.ceil(nextTotal / PAGE_SIZE));
        return;
      }

      setFans(res.data);
      setTotal(nextTotal);
      setPage(target);
      document.getElementById(SCROLL_ID)?.scrollTo({ top: 0 });
    },
    [color, search]
  );

  useEffect(() => {
    const id = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  // Cleared on close, so reopening — or opening another card — starts from the full list.
  useEffect(() => {
    if (visible) return;
    setQuery('');
    setSearch('');
  }, [visible]);

  // Also re-runs when the search changes (through `loadPage`), back to page 1 of the results.
  useEffect(() => {
    if (!visible) return;
    setFans([]);
    setTotal(0);
    setPage(1);
    loadPage(1);
  }, [visible, loadPage]);

  function handleRevoked() {
    // Re-read the same page so the next fan moves up to fill the gap and the count stays true.
    loadPage(page);
    onChanged && onChanged();
  }

  return (
    <Sidepanel visible={visible} wid={520} pad="0" closeModal={onClose} contentId={SCROLL_ID}>
      <Col pos="sticky" bg="#fff" top="0" zIndex={2} wid="100%">
        <Row between center wid="100%" pad="14px 20px">
          <Row center gap="10px">
            {color && <P4PDayGiftBadge giftColor={color} />}
            <Col start>
              <Text h3 marg="0">
                {color ? title || color : t('admin.p4pDay.fans.allTitle')}
              </Text>
              <Text c1 col={theme.GREY_60} marg="0">
                {search
                  ? t('admin.p4pDay.fans.matches', { count: total.toLocaleString() })
                  : t('admin.p4pDay.fans.count', { count: total.toLocaleString() })}
              </Text>
            </Col>
          </Row>
          <CloseBtn type="button" aria-label={t('admin.p4pDay.fans.close')} onClick={onClose}>
            <Icon type="close" dimensions={14} col={theme.GREY_80} />
          </CloseBtn>
        </Row>

        {/* Same search box as the Subscription Plans admin screen. */}
        <div className="relative w-full px-5 pb-3">
          <svg
            className="absolute left-8 top-[17px] -translate-y-1/2 w-3.5 h-3.5 text-[#97A3B5] pointer-events-none"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
          >
            <circle cx="7" cy="7" r="5" />
            <path d="M11 11l3.5 3.5" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('admin.p4pDay.fans.searchPlaceholder')}
            aria-label={t('admin.p4pDay.fans.searchPlaceholder')}
            className="w-full border border-[#B0B8C4] rounded-md pl-9 pr-8 py-[7px] text-[13px] text-[#353E4A] placeholder-[#97A3B5] bg-[#F9FAFB] focus:bg-white focus:outline-none focus:border-[#97A3B5] focus:ring-1 focus:ring-[#B5BDCA] transition-colors [&::-webkit-search-cancel-button]:hidden"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label={t('admin.p4pDay.fans.searchClear')}
              className="absolute right-7 top-[17px] -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded text-[#7989A0] hover:bg-[#F0F2F4] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#97A3B5]"
            >
              <svg
                className="w-3 h-3"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <path d="M2 2l8 8M10 2l-8 8" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
        <Divider />
      </Col>

      <Col wid="100%" pad="16px 20px 24px" gap="12px">
        {loading && fans.length === 0 ? (
          <Col centerAll pad="40px 0">
            <Spinner2 col={theme.SECONDARY} />
          </Col>
        ) : fans.length === 0 ? (
          <Col centerAll pad="40px 0">
            <Text col={theme.GREY_60}>
              {search
                ? t('admin.p4pDay.fans.noMatches', { query: search })
                : t('admin.p4pDay.fans.empty')}
            </Text>
          </Col>
        ) : (
          <>
            {fans.map((fan) => (
              <FanRow key={fan._id} fan={fan} showBadge={!color} onRevoked={handleRevoked} />
            ))}

            {totalPages > 1 && (
              <Row center between wid="100%" marg="8px 0 0">
                <Text c1 col={theme.GREY_60} marg="0">
                  {t('admin.p4pDay.fans.pageRange', {
                    from: ((page - 1) * PAGE_SIZE + 1).toLocaleString(),
                    to: Math.min(page * PAGE_SIZE, total).toLocaleString(),
                    total: total.toLocaleString()
                  })}
                </Text>
                <Row center gap="4px">
                  <PageBtn
                    type="button"
                    aria-label={t('admin.p4pDay.fans.prevPage')}
                    disabled={page <= 1 || loading}
                    onClick={() => loadPage(page - 1)}
                  >
                    ‹
                  </PageBtn>
                  {getPageNumbers(page, totalPages).map((n, i) =>
                    n === '...' ? (
                      <Text key={`gap-${i}`} c1 col={theme.GREY_60} marg="0 2px">
                        …
                      </Text>
                    ) : (
                      <PageBtn
                        key={n}
                        type="button"
                        active={n === page}
                        disabled={loading}
                        onClick={() => n !== page && loadPage(n)}
                      >
                        {n}
                      </PageBtn>
                    )
                  )}
                  <PageBtn
                    type="button"
                    aria-label={t('admin.p4pDay.fans.nextPage')}
                    disabled={page >= totalPages || loading}
                    onClick={() => loadPage(page + 1)}
                  >
                    ›
                  </PageBtn>
                </Row>
              </Row>
            )}
          </>
        )}
      </Col>
    </Sidepanel>
  );
};

export default P4PDayFansPanel;
